import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]", className)}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: "right" | "left";
  width?: string;
}

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
  // Default width bumped from 44rem (704px) to 48rem (768px): most
  // drawers in this app surface logs, traces or JSON dumps where the
  // extra horizontal room avoids constant word-wrap and horizontal
  // scrolling. `max-w-[95vw]` still keeps it inside the viewport on
  // small screens. Drawers that need MORE room can pass a wider
  // `width=` prop (e.g. `w-[60rem]` for the workflow run trace).
>(({ className, children, side = "right", width = "w-[48rem]", ...props }, ref) => (
  <DialogPrimitive.Portal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed z-50 flex flex-col bg-elevated shadow-[0_8px_32px_-4px_rgba(0,0,0,0.3)]",
        side === "right" ? "right-0 border-l border-border/60" : "left-0 border-r border-border/60",
        "top-0 bottom-0",
        width,
        "max-w-[95vw]",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-3 top-3 rounded p-1 hover:bg-muted">
        <X className="h-4 w-4" strokeWidth={1.5} />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContent.displayName = "SheetContent";

export const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // Padding aligned with SheetBody (px-5 sm:px-6) so labels in the
  // header line up vertically with content below them.
  <div
    className={cn(
      "flex flex-col gap-1 border-b border-border/60 px-5 sm:px-6 py-3",
      className,
    )}
    {...props}
  />
);

export const SheetTitle = DialogPrimitive.Title;
export const SheetDescription = DialogPrimitive.Description;

export const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // Default padding so content doesn't kiss the drawer edges. Callers
  // who need to render edge-to-edge (e.g. a code editor) can override
  // with `className="p-0"`.
  <div className={cn("flex-1 overflow-auto px-5 sm:px-6 py-4", className)} {...props} />
);

export const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex items-center justify-end gap-2 border-t border-border/60 px-5 sm:px-6 h-12",
      className,
    )}
    {...props}
  />
);
